/**
 * Pure, browser-free aggregation helpers for the check-detail view (Req 12.2, 12.4).
 *
 * These reimplement — in the SPA, over the account-scoped read **DTOs** — the same history/trend and
 * per-Global_Location roll-ups that the backend computes in `@soroush/services`
 * `dashboard/view-models.ts`. They are duplicated here on purpose: the backend builders operate on
 * the branded domain `Result` entity and run the result through `redactForDisplay`, pulling in
 * `../ingestion` / `../api` types; the dashboard instead consumes the already-redacted, plain-string
 * {@link ResultDto} the read API returns. Reimplementing the ~30 lines of arithmetic keeps the SPA
 * bundle free of any backend/AWS code (we never import `@soroush/services`) while preserving the
 * exact semantics — nearest-rank percentiles, `degraded` counting as up, an empty set reading as
 * 100% uptime — verified against the backend in `aggregation.test.ts`.
 */

import type { GlobalLocation, ResultStatus } from '@soroush/core';

/** The minimal result shape the aggregates need; {@link ResultDto} is structurally assignable. */
export interface ResultSummary {
  resultId: string;
  timestamp: string;
  location: GlobalLocation;
  status: ResultStatus;
  durationMs: number;
}

/** Count of results by Result_Status over a set of results. */
export interface StatusCounts {
  passing: number;
  degraded: number;
  failing: number;
}

/**
 * Latency statistics over a set of results' `durationMs` (Req 12.2 performance trends). Percentiles
 * use the nearest-rank method on the sorted samples. An empty set yields all-zero stats.
 */
export interface LatencyStats {
  count: number;
  minMs: number;
  maxMs: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
}

/** One point in the history series (a single result), time-ordered. */
export interface HistoryPoint {
  resultId: string;
  timestamp: string;
  location: GlobalLocation;
  status: ResultStatus;
  durationMs: number;
}

/** The history/trend view for a Check over a selected time range (Req 12.2). */
export interface HistoryView {
  total: number;
  statusCounts: StatusCounts;
  /** Non-failing fraction as a percentage (degraded counts as up, per Check_State semantics). */
  uptimePct: number;
  latency: LatencyStats;
  /** Results in ascending time order — the trend series the dashboard renders. */
  series: HistoryPoint[];
}

/** Per-location roll-up for a Check (Req 12.4). */
export interface LocationRow {
  location: GlobalLocation;
  total: number;
  statusCounts: StatusCounts;
  uptimePct: number;
  latency: LatencyStats;
  latestStatus?: ResultStatus;
  latestTimestamp?: string;
}

function emptyStatusCounts(): StatusCounts {
  return { passing: 0, degraded: 0, failing: 0 };
}

/** Tally results by Result_Status. */
export function tallyStatuses(results: readonly ResultSummary[]): StatusCounts {
  const counts = emptyStatusCounts();
  for (const r of results) counts[r.status] += 1;
  return counts;
}

/**
 * Uptime as a percentage: the fraction of results that did NOT fail, matching Check_State semantics
 * where a `degraded` result does not take a Check down (only `failing` does, Req 9.2). An empty set
 * is reported as `100` (no observed downtime).
 */
export function uptimePct(results: readonly ResultSummary[]): number {
  if (results.length === 0) return 100;
  const nonFailing = results.filter((r) => r.status !== 'failing').length;
  return (nonFailing / results.length) * 100;
}

/** Nearest-rank percentile (`0 < p <= 100`) over an ascending-sorted sample; `0` when empty. */
export function percentile(sortedAsc: readonly number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const rank = Math.ceil((p / 100) * sortedAsc.length);
  const index = Math.min(sortedAsc.length - 1, Math.max(0, rank - 1));
  return sortedAsc[index];
}

/** Latency min/max/avg/p50/p95 over the results' durations (nearest-rank percentiles). */
export function latencyStats(results: readonly ResultSummary[]): LatencyStats {
  if (results.length === 0) {
    return { count: 0, minMs: 0, maxMs: 0, avgMs: 0, p50Ms: 0, p95Ms: 0 };
  }
  const durations = results.map((r) => r.durationMs).sort((a, b) => a - b);
  const sum = durations.reduce((acc, d) => acc + d, 0);
  return {
    count: durations.length,
    minMs: durations[0],
    maxMs: durations[durations.length - 1],
    avgMs: sum / durations.length,
    p50Ms: percentile(durations, 50),
    p95Ms: percentile(durations, 95),
  };
}

function instantMs(timestamp: string): number {
  return Date.parse(timestamp);
}

/**
 * Build the history/trend view (Req 12.2) for a Check from its results in a selected time range. The
 * caller supplies the already range-filtered results (the read endpoint does the filtering); this
 * orders them ascending by time and derives the status/latency trend aggregates.
 */
export function buildHistoryView(results: readonly ResultSummary[]): HistoryView {
  const ordered = [...results].sort((a, b) => instantMs(a.timestamp) - instantMs(b.timestamp));
  return {
    total: ordered.length,
    statusCounts: tallyStatuses(ordered),
    uptimePct: uptimePct(ordered),
    latency: latencyStats(ordered),
    series: ordered.map((r) => ({
      resultId: r.resultId,
      timestamp: r.timestamp,
      location: r.location,
      status: r.status,
      durationMs: r.durationMs,
    })),
  };
}

/**
 * Build the per-Global_Location aggregation (Req 12.4): group the Check's results by location and
 * derive per-location status/latency roll-ups and the latest observed status. Locations are ordered
 * by name for a stable display.
 */
export function buildLocationRows(results: readonly ResultSummary[]): LocationRow[] {
  const byLocation = new Map<GlobalLocation, ResultSummary[]>();
  for (const r of results) {
    const bucket = byLocation.get(r.location);
    if (bucket) bucket.push(r);
    else byLocation.set(r.location, [r]);
  }

  return [...byLocation.entries()]
    .map(([location, group]) => {
      const latest = group.reduce((a, b) => (instantMs(b.timestamp) >= instantMs(a.timestamp) ? b : a));
      const row: LocationRow = {
        location,
        total: group.length,
        statusCounts: tallyStatuses(group),
        uptimePct: uptimePct(group),
        latency: latencyStats(group),
        latestTimestamp: latest.timestamp,
        latestStatus: latest.status,
      };
      return row;
    })
    .sort((a, b) => a.location.localeCompare(b.location));
}
